/* eslint-disable no-plusplus */
/* eslint-disable no-continue */

import React, { useContext, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';

import KebabMenu from '../../../../KebabMenu';
import { UiContext } from '../../../../../context/UiContext';
import AdminService from '../../../../../services/AdminService';
import UserService from '../../../../../services/UserService';
import classes from './styles.module.scss';
import { JobsContext } from '../../../../../context/JobsContext';
import { getTimezoneTime } from '../../../../../helpers/timezones';
import GradientSwitch from '../../../../GradientSwitch';

function getDate(date) {
  const temp = new Date(date);

  return `${temp.getDate()} ${temp
    .toLocaleString('en-US', { month: 'long' })
    .slice(0, 3)} ${temp.getFullYear()} `;
}

function formatString(str = '') {
  let result = '';

  for (let i = 0; i < str.length; i++) {
    const temp = str[i];

    if (i === 0) {
      result += str[i];

      continue;
    }

    if (str[i].toUpperCase() === temp) {
      result += ' ';
    }

    result += str[i];
  }

  return result;
}
export default function Interview({ jobApplication, job, refetch }) {
  const { showModal, showUnknownErrorModal, showNotification, setIsFetching } =
    useContext(UiContext);

  const { showScheduleInterviewModal, showFeedbackModal } =
    useContext(JobsContext);

  const { t } = useTranslation();

  const isInterviewStage = jobApplication?.status === 'Interview';
  const isClientInterviewStage = jobApplication?.status === 'ClientInterview';

  // Local state to track shared status for immediate UI updates
  const [localSharedStatus, setLocalSharedStatus] = useState({});

  // Initialize local state from props when jobApplication changes
  useEffect(() => {
    const initialStatus = {};
    jobApplication?.jobApplicationInterviews?.forEach((interview) => {
      interview.jobApplicationInterviewFeedbacks?.forEach((feedback) => {
        initialStatus[feedback.id] = feedback.isShared;
      });
    });
    setLocalSharedStatus(initialStatus);
  }, [jobApplication]);

  const handleClickOnFeedback = (
    interview,
    isReviewing = false,
    existingFeedback = null,
    readOnly = false
  ) => {
    showFeedbackModal({
      candidate: jobApplication,
      interview,
      job,
      refetch,
      isReviewing,
      existingFeedback,
      readOnly,
    });
  };

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
  });

  const isAuthorizedFeedbackReviewer = user?.roles?.some(
    (role) => role.key === 'recruiter' || role.key === 'admin'
  );

  const deleteFeedback = (interview, feedbackId) => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Delete Feedback'
      ),
      text: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Are you sure you want to delete?'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.deleteFeedbackFromInterview({
            jobApplicationId: jobApplication?.id,
            interviewId: interview?.id,
            feedbackId,
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Interview feedback deleted successfully'
            ),
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const shareFeedback = async (interview, feedbackId) => {
    // Get current shared status
    const currentStatus = localSharedStatus[feedbackId] || false;
    const newStatus = !currentStatus;

    // Optimistically update local state immediately
    setLocalSharedStatus((prev) => ({
      ...prev,
      [feedbackId]: newStatus,
    }));

    try {
      setIsFetching(true);
      await AdminService.shareFeedbackFromInterview({
        jobApplicationId: jobApplication?.id,
        interviewId: interview?.id,
        feedbackId,
      });

      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t(
          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Interview feedback shared successfully'
        ),
      });
    } catch (error) {
      // Revert to previous state on error
      setLocalSharedStatus((prev) => ({
        ...prev,
        [feedbackId]: currentStatus,
      }));
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const acceptInvite = async (interview, inviteId) => {
    try {
      setIsFetching(true);
      await AdminService.changeJobApplicationInterviewInvite({
        interviewId: interview?.id,
        jobApplicationId: jobApplication?.id,
        invitationid: inviteId,
        status: 'Accepted',
      });

      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t(
          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Interview invitation accepted successfully'
        ),
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const declineInvite = async (interview, inviteId) => {
    try {
      setIsFetching(true);
      await AdminService.changeJobApplicationInterviewInvite({
        interviewId: interview?.id,
        jobApplicationId: jobApplication?.id,
        invitationid: inviteId,
        status: 'Declined',
      });

      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t(
          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Interview invitation declined successfully'
        ),
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const cancelInterview = async (interviewId) => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Cancel Interview'
      ),
      text: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Are you sure you want to cancel?'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.cancelJobApplicationInterviewNew({
            jobApplicationId: jobApplication?.id,
            interviewId,
          });
          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Interview canceled successfully'
            ),
          });
        } catch (error) {
          console.log(error);
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const deleteInterview = async (interviewId) => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Delete Interview'
      ),
      text: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Are you sure you want to delete?'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.deleteJobApplicationInterview({
            jobApplicationId: jobApplication?.id,
            interviewid: interviewId,
          });
          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Interview deleted successfully'
            ),
          });
        } catch (error) {
          console.log(error);
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  return (
    <div className={classes.interviews}>
      <div className={classes.container}>
        <h2 className={classes.title}>
          {t(
            'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Interview'
          )}
        </h2>
        {jobApplication?.jobApplicationInterviews?.length ? (
          <div className={classes.amount}>
            {jobApplication.jobApplicationInterviews.length}
          </div>
        ) : null}
      </div>
      {!jobApplication?.jobApplicationInterviews?.length ? (
        <p className={classes.noData}>
          {t(
            'dashboardComponents.Candidate.AssessmentAndInterview.Interview.No interview scheduled'
          )}
        </p>
      ) : (
        <>
          <div className={classes.interviewList}>
            {jobApplication?.jobApplicationInterviews
              .sort(
                (a, b) => new Date(a.interviewDate) - new Date(b.interviewDate)
              )
              .map((interview) => {
                const { status, jobApplicationInterviewFeedbacks } = interview;

                const isInterviewerOrOrganizer =
                  user?.id === interview?.organizer?.id ||
                  interview?.users?.some((usr) => usr.id === user?.id) ||
                  interview?.jobApplicationInterviewFeedbacks?.some(
                    (feedback) => feedback.userId === user?.id
                  );

                const isOrganizer = user?.id === interview?.organizer?.id;

                let organizerName = interview.organizer
                  ? `${interview.organizer?.userProfile?.firstName} ${interview.organizer?.userProfile?.lastName}`
                  : null;

                if (status === 'Requested') {
                  organizerName = interview.requestor
                    ? `${interview.requestor?.userProfile?.firstName} ${interview.requestor?.userProfile?.lastName}`
                    : null;
                }

                let adjustedStatus = status;
                const interviewersCount =
                  interview.users.length +
                  interview.externalInterviewers.length +
                  interview.companyManagers.length;

                if (
                  (status === 'Scheduled' ||
                    status === 'Accepted' ||
                    status === 'Rescheduled') &&
                  Date.parse(interview.interviewDate) < new Date()
                ) {
                  if (
                    interview.jobApplicationInterviewFeedbacks?.length !== 0
                  ) {
                    if (
                      interview.jobApplicationInterviewFeedbacks.some(
                        (feedback) => feedback.reviewedAt === null
                      )
                    ) {
                      adjustedStatus = 'AwaitingReview';
                    } else if (
                      interviewersCount <=
                      interview.jobApplicationInterviewFeedbacks.filter(
                        (feedback) => feedback.reviewedAt !== null
                      ).length
                    ) {
                      adjustedStatus = 'FeedbackCompleted';
                    } else {
                      adjustedStatus = 'AwaitingFeedback';
                    }
                  } else {
                    adjustedStatus = 'AwaitingFeedback';
                  }
                }

                const kebabOptions = [
                  {
                    label: t('common.viewDetails'),
                    value: 'viewDetails',
                    hidden: interview.status === 'Requested',
                    onClick: () => {
                      showScheduleInterviewModal({
                        candidate: jobApplication,
                        refetch,
                        initialContactType: interview.contactType,
                        editableInterviewProp: interview,
                        isViewDetailsProp: true,
                      });
                    },
                  },
                  {
                    label: t('common.edit'),
                    value: 'edit',
                    hidden:
                      Date.parse(interview.interviewDate) < new Date() ||
                      !isOrganizer ||
                      interview.status === 'Canceled' ||
                      interview.status === 'Requested',
                    onClick: () => {
                      if (isInterviewStage) {
                        showScheduleInterviewModal({
                          candidate: jobApplication,
                          refetch,
                          initialContactType: 'Internal',
                          editableInterviewProp: interview,
                        });
                      }
                      if (isClientInterviewStage) {
                        showScheduleInterviewModal({
                          candidate: jobApplication,
                          refetch,
                          initialContactType: 'Client',
                          editableInterviewProp: interview,
                        });
                      }
                    },
                  },
                  {
                    label: t('common.scheduleRequestedInterview'),
                    value: 'scheduleRequestedInterview',
                    hidden: interview.status !== 'Requested',
                    onClick: () => {
                      showScheduleInterviewModal({
                        candidate: jobApplication,
                        refetch,
                        initialContactType: 'Client',
                        editableInterviewProp: interview,
                      });
                    },
                  },
                  {
                    label: t('common.submitFeedback'),
                    value: 'submitFeedback',
                    hidden:
                      !isInterviewerOrOrganizer ||
                      interview.status === 'Requested' ||
                      interview.status === 'Canceled' ||
                      interview.jobApplicationInterviewFeedbacks?.some(
                        (feedback) => feedback.userId === user?.id
                      ),
                    disabled: Date.parse(interview.interviewDate) > new Date(),
                    onClick: () => {
                      handleClickOnFeedback(interview);
                    },
                  },
                  {
                    label: t('common.cancel'),
                    value: 'cancel',
                    hidden:
                      Date.parse(interview.interviewDate) < new Date() ||
                      !isOrganizer ||
                      interview.status === 'Canceled' ||
                      interview.status === 'Requested',
                    onClick: () => cancelInterview(interview.id),
                    warning: true,
                  },
                  {
                    label: t('common.delete'),
                    value: 'delete',
                    hidden: !isOrganizer || interview.status === 'Requested',
                    onClick: () => deleteInterview(interview.id),
                    danger: true,
                  },
                ];

                const hasVisibleKebabOption = kebabOptions.some(
                  (option) => !option.hidden
                );

                return (
                  <div className={classes.interview} key={interview?.id}>
                    <div className={classes.upper}>
                      <p className={classes.date}>
                        {status === 'Requested'
                          ? getDate(interview.createdAt)
                          : getDate(interview.interviewDate)}
                      </p>
                      <div className={classes.settings}>
                        {!!jobApplicationInterviewFeedbacks.length && (
                          <div className={classNames(classes.rating)}>
                            {(
                              jobApplicationInterviewFeedbacks.reduce(
                                (acc, feedback) =>
                                  acc + feedback.feedbackRating,
                                0
                              ) / jobApplicationInterviewFeedbacks.length
                            ).toFixed(1)}
                          </div>
                        )}
                        <div
                          className={classNames(classes.status, {
                            [classes.scheduled]: adjustedStatus === 'Scheduled',
                            [classes.scheduled]:
                              adjustedStatus === 'Rescheduled',
                            [classes.declined]: adjustedStatus === 'Declined',
                            [classes.accepted]: adjustedStatus === 'Accepted',
                            [classes.pass]: adjustedStatus === 'Pass',
                            [classes.fail]: adjustedStatus === 'Fail',
                            [classes.canceled]: adjustedStatus === 'Canceled',
                            [classes.changeRequest]:
                              adjustedStatus === 'ChangeRequest',
                            [classes.awaitingReview]:
                              adjustedStatus === 'AwaitingReview',
                            [classes.feedbackInProgress]:
                              adjustedStatus === 'FeedbackInProgress',
                            [classes.awaitingFeedback]:
                              adjustedStatus === 'AwaitingFeedback',
                            [classes.feedbackReviewed]:
                              adjustedStatus === 'FeedbackReviewed',
                          })}
                        >
                          {t(`common.interviewStatuses.${adjustedStatus}`)}
                        </div>

                        {hasVisibleKebabOption && (
                          <KebabMenu options={kebabOptions} />
                        )}
                      </div>
                    </div>
                    <div
                      className={classNames(classes.lower, {
                        [classes.scheduled]: adjustedStatus === 'Scheduled',
                        [classes.declined]: adjustedStatus === 'Declined',
                        [classes.accepted]: adjustedStatus === 'Accepted',
                        [classes.pass]: adjustedStatus === 'Pass',
                        [classes.scheduled]: adjustedStatus === 'Rescheduled',
                        [classes.fail]: adjustedStatus === 'Fail',
                        [classes.canceled]: adjustedStatus === 'Canceled',
                        [classes.changeRequest]:
                          adjustedStatus === 'ChangeRequest',
                        [classes.requested]: adjustedStatus === 'Requested',
                        [classes.awaitingReview]:
                          adjustedStatus === 'AwaitingReview',
                        [classes.feedbackInProgress]:
                          adjustedStatus === 'FeedbackInProgress',
                        [classes.awaitingFeedback]:
                          adjustedStatus === 'AwaitingFeedback',
                        [classes.feedbackReviewed]:
                          adjustedStatus === 'FeedbackReviewed',
                      })}
                    >
                      <div className={classes.interviewDetails}>
                        <div
                          className={classNames(classes.col, classes.leftPanel)}
                        >
                          <h3 className={classes.name}>{job?.jobTitle}</h3>
                          <p className={classes.description}>
                            {jobApplication.jobPost?.company?.name}
                          </p>
                          <p
                            className={classNames(
                              classes.description,
                              classes.muted
                            )}
                          >
                            {t('common.jobId')}: {job?.id}
                          </p>
                        </div>
                        {interview.status !== 'Requested' && (
                          <div className={classes.col}>
                            <h3 className={classes.name}>
                              {getDate(interview.interviewDate)}{' '}
                              {`${getTimezoneTime(
                                interview?.startTime,
                                user?.userProfile?.timezone
                              )} - ${getTimezoneTime(
                                interview?.endTime,
                                user?.userProfile?.timezone
                              )}`}
                            </h3>

                            <p
                              className={classNames(
                                classes.description,
                                classes.muted
                              )}
                            >
                              {`${
                                +interview?.endTime?.split(':')[0] -
                                +interview?.startTime?.split(':')[0]
                              } ${t(
                                'dashboardComponents.Candidate.AssessmentAndInterview.Interview.hr'
                              )} ${Math.abs(
                                +interview?.endTime?.split(':')[1] -
                                  +interview?.startTime?.split(':')[1]
                              )} ${t(
                                'dashboardComponents.Candidate.AssessmentAndInterview.Interview.mins'
                              )}`}
                            </p>
                          </div>
                        )}
                        <div className={classes.col}>
                          <h3 className={classes.name}>
                            {formatString(interview.type)}
                          </h3>
                        </div>
                        <div className={classes.col}>
                          <h3 className={classes.name}>
                            {formatString(interview?.contactType)} Interview
                          </h3>
                        </div>
                        <div className={classes.col}>
                          <h3 className={classes.name}>
                            {organizerName ?? ''}
                          </h3>
                          <p className={classes.description}>
                            {interview.status === 'Requested'
                              ? t(
                                  'dashboardComponents.Candidate.AssessmentAndInterview.Interview.RequestedBy'
                                )
                              : t(
                                  'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Organizer'
                                )}
                          </p>
                        </div>
                      </div>
                      {interview.status !== 'Requested' && (
                        <div className={classes.lowerSection}>
                          <h3 className={classes.title}>
                            {t(
                              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Participants'
                            )}
                          </h3>

                          <div className={classes.invitations}>
                            {interview.jobApplicationInterviewInvites.map(
                              (invite) => {
                                let participantName = '';
                                switch (invite?.contactType) {
                                  case 'Internal':
                                    participantName = interview.users?.find(
                                      (usr) => usr.id === invite.userId
                                    ).userProfile?.fullName;
                                    break;
                                  case 'External':
                                    participantName = `${
                                      interview.externalInterviewers?.find(
                                        (externalInterviewer) =>
                                          externalInterviewer?.id ===
                                          invite.externalInterviewerId
                                      ).firstName
                                    } ${
                                      interview.externalInterviewers?.find(
                                        (externalInterviewer) =>
                                          externalInterviewer?.id ===
                                          invite.externalInterviewerId
                                      ).lastName
                                    }`;
                                    break;
                                  case 'Client':
                                    participantName = `${
                                      interview.companyManagers?.find(
                                        (companyManager) =>
                                          companyManager?.id ===
                                          invite.companyManagerId
                                      ).firstName
                                    } ${
                                      interview.companyManagers?.find(
                                        (companyManager) =>
                                          companyManager?.id ===
                                          invite.companyManagerId
                                      ).lastName
                                    }`;
                                    break;
                                  default:
                                    participantName =
                                      jobApplication?.user?.userProfile
                                        ?.fullName;
                                    break;
                                }
                                let participantDesignation = null;
                                switch (invite?.contactType) {
                                  case 'Internal':
                                    participantDesignation =
                                      interview.users?.find(
                                        (usr) => usr.id === invite.userId
                                      ).userProfile?.designation;
                                    break;
                                  case 'External':
                                    participantDesignation = `${
                                      interview.externalInterviewers?.find(
                                        (externalInterviewer) =>
                                          externalInterviewer?.id ===
                                          invite.externalInterviewerId
                                      ).firstName
                                    }`;
                                    break;
                                  case 'Client':
                                    participantDesignation = `${
                                      interview.companyManagers?.find(
                                        (companyManager) =>
                                          companyManager?.id ===
                                          invite.companyManagerId
                                      ).designation
                                    }`;
                                    break;
                                  default:
                                    break;
                                }
                                return (
                                  <div
                                    className={classes.invitation}
                                    key={invite.id}
                                  >
                                    <div className={classes.col}>
                                      <p className={classes.title}>
                                        {participantName}{' '}
                                        {participantDesignation
                                          ? `(${participantDesignation})`
                                          : ''}
                                      </p>
                                      <p
                                        className={classNames(
                                          classes.description,
                                          classes.muted
                                        )}
                                      >
                                        {t(
                                          `dashboardComponents.Candidate.AssessmentAndInterview.Interview.InterviewContactTypeOptions.${invite.contactType}`
                                        )}
                                      </p>
                                    </div>
                                    <p
                                      className={classNames(classes.status, {
                                        [classes.pending]:
                                          invite.status === 'Pending',
                                        [classes.declined]:
                                          invite.status === 'Declined',
                                        [classes.accepted]:
                                          invite.status === 'Accepted',
                                      })}
                                    >
                                      {t(
                                        `dashboardComponents.Candidate.AssessmentAndInterview.Interview.InviteStatusOptions.${invite.status}`
                                      )}
                                    </p>
                                    <div className={classes.actions}>
                                      {isOrganizer &&
                                        interview.status !== 'Canceled' &&
                                        Date.parse(interview.interviewDate) >
                                          new Date() && (
                                          <KebabMenu
                                            variant="small"
                                            options={[
                                              {
                                                label: t(
                                                  invite.contactType ===
                                                    'Internal' &&
                                                    invite.userId === user?.id
                                                    ? 'common.accept'
                                                    : 'common.acceptOnBehalf'
                                                ),
                                                value: 'accept',
                                                success: true,
                                                hidden:
                                                  invite.status === 'Accepted',
                                                onClick: () => {
                                                  acceptInvite(
                                                    interview,
                                                    invite.id
                                                  );
                                                },
                                              },
                                              {
                                                label: t(
                                                  invite.contactType ===
                                                    'Internal' &&
                                                    invite.userId === user?.id
                                                    ? 'common.decline'
                                                    : 'common.declineOnBehalf'
                                                ),
                                                value: 'decline',
                                                warning: true,
                                                hidden:
                                                  invite.status === 'Declined',
                                                onClick: () => {
                                                  declineInvite(
                                                    interview,
                                                    invite.id
                                                  );
                                                },
                                              },
                                            ]}
                                          />
                                        )}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}
                      {interview.jobApplicationInterviewFeedbacks.length ? (
                        <div className={classes.lowerSection}>
                          <h3 className={classes.title}>
                            {t(
                              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedbacks'
                            )}
                          </h3>
                          <div className={classes.feedbackList}>
                            {interview.jobApplicationInterviewFeedbacks.map(
                              (feedback) => {
                                let feedbackTypeText = '';
                                if (feedback.feedbackType === 'Internal') {
                                  feedbackTypeText = 'Internal Feedback';
                                } else if (
                                  feedback.feedbackType === 'External'
                                ) {
                                  feedbackTypeText = 'External Feedback';
                                } else if (
                                  interview.companyManagers.some(
                                    (companyManager) =>
                                      companyManager.user?.id ===
                                      feedback.userId
                                  )
                                ) {
                                  feedbackTypeText = 'Client Feedback';
                                }

                                const interviewerName =
                                  feedback.user?.userProfile?.fullName ??
                                  'Author Uknown';

                                let feedbackRecommendationTypeText = '';
                                if (
                                  feedback.recommendationType === 'StrongHire'
                                ) {
                                  feedbackRecommendationTypeText =
                                    'Strong Hire';
                                } else if (
                                  feedback.recommendationType === 'Hire'
                                ) {
                                  feedbackRecommendationTypeText = 'Hire';
                                } else if (
                                  feedback.recommendationType === 'Neutral'
                                ) {
                                  feedbackRecommendationTypeText = 'Neutral';
                                } else if (
                                  feedback.recommendationType === 'NotHire'
                                ) {
                                  feedbackRecommendationTypeText = 'Not Hire';
                                } else {
                                  feedbackRecommendationTypeText =
                                    'Not Specified';
                                }

                                return (
                                  <div
                                    className={classes.feedback}
                                    key={feedback.id}
                                  >
                                    <div className={classes.feedbackCol}>
                                      <p className={classes.title}>
                                        {interviewerName}
                                      </p>
                                      <p
                                        className={classNames(
                                          classes.description,
                                          classes.muted
                                        )}
                                      >
                                        {feedbackTypeText}
                                      </p>
                                    </div>
                                    <div className={classes.feedbackCol}>
                                      <p className={classes.title}>
                                        {feedback.feedbackRating}
                                      </p>
                                      <p
                                        className={classNames(
                                          classes.description,
                                          classes.muted
                                        )}
                                      >
                                        {t(
                                          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Rating'
                                        )}
                                      </p>
                                    </div>
                                    <div className={classes.feedbackCol}>
                                      <p className={classes.title}>
                                        {feedbackRecommendationTypeText}
                                      </p>
                                      <p
                                        className={classNames(
                                          classes.description,
                                          classes.muted
                                        )}
                                      >
                                        {t(
                                          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Recommendation Type'
                                        )}
                                      </p>
                                    </div>
                                    <div className={classes.feedbackCol}>
                                      <p className={classes.title}>
                                        {feedback.reviewedAt
                                          ? t('common.reviewed')
                                          : t('common.notReviewed')}
                                      </p>
                                      <p
                                        className={classNames(
                                          classes.description,
                                          classes.muted
                                        )}
                                      >
                                        {t(
                                          'dashboardComponents.Candidate.AssessmentAndInterview.Interview.status'
                                        )}
                                      </p>
                                    </div>
                                    {feedback.feedbackType !== 'Client' ? (
                                      <div className={classes.feedbackCol}>
                                        <p className={classes.title}>
                                          <GradientSwitch
                                            readOnly={
                                              feedback.reviewedAt === null
                                            }
                                            isEnabled={
                                              localSharedStatus[feedback.id] !==
                                              undefined
                                                ? localSharedStatus[feedback.id]
                                                : feedback.isShared
                                            }
                                            onClick={() => {
                                              shareFeedback(
                                                interview,
                                                feedback.id
                                              );
                                            }}
                                          />
                                        </p>
                                        <p
                                          className={classNames(
                                            classes.description,
                                            classes.muted
                                          )}
                                        >
                                          {t(
                                            'dashboardComponents.Candidate.AssessmentAndInterview.Interview.ShareFeedback'
                                          )}
                                        </p>
                                      </div>
                                    ) : (
                                      <div className={classes.feedbackCol} />
                                    )}
                                    <div className={classes.feedbackCol}>
                                      <KebabMenu
                                        variant="small"
                                        options={[
                                          {
                                            label: t('common.edit'),
                                            value: 'edit',
                                            hidden:
                                              feedback.reviewedAt !== null ||
                                              feedback.userId !== user?.id,
                                            onClick: () => {
                                              handleClickOnFeedback(
                                                interview,
                                                false,
                                                feedback
                                              );
                                            },
                                          },
                                          {
                                            label: t('common.review'),
                                            value: 'review',
                                            hidden:
                                              feedback.reviewedAt !== null ||
                                              !isAuthorizedFeedbackReviewer,
                                            onClick: () => {
                                              handleClickOnFeedback(
                                                interview,
                                                true,
                                                feedback
                                              );
                                            },
                                          },
                                          {
                                            label: t('common.viewFeedback'),
                                            value: 'viewFeedback',
                                            hidden:
                                              feedback.reviewedAt === null ||
                                              !isAuthorizedFeedbackReviewer,
                                            onClick: () => {
                                              handleClickOnFeedback(
                                                interview,
                                                true,
                                                feedback,
                                                true
                                              );
                                            },
                                          },
                                          {
                                            label: t('common.delete'),
                                            value: 'delete',
                                            onClick: () => {
                                              deleteFeedback(
                                                interview,
                                                feedback.id
                                              );
                                            },
                                            danger: true,
                                          },
                                        ]}
                                      />
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}
